import React, { useEffect, useState, Children, createElement } from 'react';
import { Header } from '../components/Header';
import { VideoPlayer } from '../components/VideoPlayer';
import { CustomVideoPlayer } from '../components/CustomVideoPlayer';
import { HeroHighlight, Highlight } from '../components/ui/HeroHighlight';
import { BookCallButton } from '../components/ui/BookCallButton';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
const vttContent1 = `WEBVTT

00:00:00.000 --> 00:00:05.000
All right, this next step is where we need to create two subdomains for your website

00:00:05.000 --> 00:00:09.000
that is going to help us stay compliant when it comes to texting.

00:00:09.000 --> 00:00:13.000
Now, if you do not manage the website, then just go ahead and fill out the form below

00:00:13.000 --> 00:00:18.000
that is going to give us a contact for the person that manages the website.

00:00:18.000 --> 00:00:22.000
And if that person is you and you would like to go ahead and get this set up,

00:00:22.000 --> 00:00:26.000
then just go ahead and follow the next video that I'm going to present here in a second.

00:00:26.000 --> 00:00:30.000
That's going to walk you step by step through the process of how to create

00:00:30.000 --> 00:00:34.000
these two separate subdomains. It's going to be located in your DNS settings.

00:00:34.000 --> 00:00:38.000
We look forward to working with you guys and excited for this next step.`;
const vttContent2 = `WEBVTT

00:00:00.000 --> 00:00:05.000
All right everyone, this is the final step to creating the two subdomains that are necessary

00:00:05.000 --> 00:00:10.000
for us to have to connect back to our ATS in order for us to be compliant for texting.

00:00:10.000 --> 00:00:15.000
So, I'm going to use an example here and show you a step-by-step walkthrough of how exactly

00:00:15.000 --> 00:00:20.000
you're going to be able to add those domains, and that's going to be an example here inside of Cloudflare.

00:00:20.000 --> 00:00:25.000
Now, if you have GoDaddy, if you have Wix, it's going to look very similar and it's going to be pretty much the same exact process.

00:00:25.000 --> 00:00:30.000
So, if you log into your website hosting, you're going to hit the domains section tab.

00:00:30.000 --> 00:00:35.000
You're going to select the website domain that you want to add these records to.

00:00:35.000 --> 00:00:40.000
And once you're on here, you're going to look for a setting that's called DNS.

00:00:40.000 --> 00:00:45.000
Inside this DNS, you're going to drop down into records, and there's going to be a simple process here

00:00:45.000 --> 00:00:50.000
that we're going to go down to the add record section. We're going to select the type to CNAME.

00:00:50.000 --> 00:00:55.000
We're going to then type in "apply", and then we need to also add in this target right here,

00:00:55.000 --> 00:01:00.000
which is sites.ludicrous.cloud. Once we copy that over into this side,

00:01:00.000 --> 00:01:05.000
it's going to allow us to then sync this back to our ATS and this subdomain will now allow us to be A2P certified.

00:01:05.000 --> 00:01:10.000
Another thing to do is to make sure that you uncheck the proxy status if you're on Cloudflare.

00:01:10.000 --> 00:01:15.000
It should also look similar to anything inside of GoDaddy, etc. So just make sure that's unchecked.

00:01:15.000 --> 00:01:20.000
Now, once this one's done, hit save. Move on to the next, it will be the same type, and then it will say "screening" instead.

00:01:20.000 --> 00:01:25.000
And then we want to make sure to add in the data here, which is the type or target,

00:01:25.000 --> 00:01:30.000
which is going to be alias.videoask.com. Now we add that over into the target section,

00:01:30.000 --> 00:01:35.000
unproxy it, and then hit save. Same exact process.

00:01:35.000 --> 00:01:40.000
So if you have any questions, just reach out to me directly and we'll get this settled for you.`;
export function DnsPage() {
  const [vttUrl1, setVttUrl1] = useState('');
  const [vttUrl2, setVttUrl2] = useState('');
  const [activeTab, setActiveTab] = useState(1);
  useEffect(() => {
    window.scrollTo(0, 0);
    // Load Typeform embed script
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);
    const blob1 = new Blob([vttContent1], {
      type: 'text/vtt'
    });
    const url1 = URL.createObjectURL(blob1);
    setVttUrl1(url1);
    const blob2 = new Blob([vttContent2], {
      type: 'text/vtt'
    });
    const url2 = URL.createObjectURL(blob2);
    setVttUrl2(url2);
    return () => {
      URL.revokeObjectURL(url1);
      URL.revokeObjectURL(url2);
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleNextStep = () => {
    setIsDialogOpen(true);
  };
  const handleConfirm = () => {
    setIsDialogOpen(false);
    navigate('/indeed');
  };
  const openInNewWindow = (url: string) => {
    window.open(url, '_blank');
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100,
        duration: 0.4
      }
    }
  };
  return <div className="w-full min-h-screen overflow-y-auto" data-id="element-244">
      <HeroHighlight data-id="element-245">
        <div className="min-h-screen flex flex-col" data-id="element-246">
          <Header data-id="element-247" />
          <motion.main className="flex-1 flex flex-col justify-center max-w-5xl mx-auto px-4 py-4 w-full" initial="hidden" animate="visible" variants={containerVariants} data-id="element-248">
            <motion.div variants={itemVariants} data-id="element-249">
              <div className="flex justify-center mb-2 sm:mb-3 items-center" data-id="element-250">
                <div className="flex items-center" data-id="element-251">
                  <motion.button onClick={() => navigate('/onboarding')} className="mr-2 h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} data-id="element-252">
                    <ChevronLeftIcon className="w-4 h-4" data-id="element-253" />
                  </motion.button>
                  <div className="inline-flex items-center px-3 sm:px-5 py-1 sm:py-1.5 rounded-full border border-white/30 bg-black/30 backdrop-blur-sm" data-id="element-254">
                    <span className="text-xs sm:text-sm font-medium text-white" data-id="element-255">
                      STEP
                    </span>
                    <div className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 mx-1 sm:mx-1.5 rounded-full bg-white" data-id="element-256">
                      <span className="text-xs sm:text-sm font-bold text-gray-900" data-id="element-257">
                        3
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-white" data-id="element-258">
                      OF 4
                    </span>
                  </div>
                </div>
              </div>
              <h1 className="text-center text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 max-w-3xl mx-auto leading-[1.3] text-white" data-id="element-259">
                Instructions for Creating Two Subdomains
              </h1>

              <div className="relative max-w-3xl mx-auto w-full" data-id="element-260">
                <div className="flex gap-2 mb-3 justify-center" data-id="element-261">
                  <button onClick={() => setActiveTab(1)} className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${activeTab === 1 ? 'bg-[#7fe0c3] text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-262">
                    Part 1
                  </button>
                  <button onClick={() => setActiveTab(2)} className={`px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors ${activeTab === 2 ? 'bg-[#7fe0c3] text-gray-900' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-263">
                    Part 2
                  </button>
                  <button onClick={() => window.open('https://docs.google.com/document/d/1-02JPxIIc_CGPcETlap7-glaXhAC-H2ODhZpfSeGfsY/edit?usp=sharing', '_blank')} className="px-4 py-1.5 text-sm rounded-lg font-semibold transition-colors bg-white/10 text-white hover:bg-white/20" data-id="element-264">
                    SOP
                  </button>
                </div>

                <div className={activeTab === 1 ? 'block' : 'hidden'} data-id="element-265">
                  <VideoPlayer mediaId="iz7a7d3tjo" onEnded={() => setActiveTab(2)} autoPlay={true} data-id="element-266" />
                </div>
                <div className={activeTab === 2 ? 'block' : 'hidden'} data-id="element-267">
                  <CustomVideoPlayer src="https://cdn.magicpatterns.com/uploads/8CcHvSp5MZC7ReFN3aXRxH/How%20to%20Create%20Subdomains%20in%20CloudFlare%20%F0%9F%8C%90.mp4" vttUrl={vttUrl2} autoPlay={activeTab === 2} data-id="element-268" />
                </div>
                <motion.button onClick={handleNextStep} className="hidden md:flex flex-col items-center gap-1 px-3 py-4 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors cursor-pointer absolute -right-16" style={{
                top: '50%',
                y: '-50%'
              }} whileHover={{
                scale: 1.05,
                x: 4
              }} whileTap={{
                scale: 0.95
              }} data-id="element-269">
                  <ChevronRightIcon className="w-6 h-6" data-id="element-270" />
                  <span className="text-xs font-semibold whitespace-nowrap" data-id="element-271">
                    Next
                  </span>
                </motion.button>
              </div>
              <motion.button onClick={handleNextStep} className="md:hidden flex items-center justify-center gap-2 mx-auto mt-3 py-2.5 px-6 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors cursor-pointer" whileTap={{
              scale: 0.95
            }} data-id="element-272">
                <span className="text-sm font-semibold" data-id="element-273">Next</span>
                <ChevronRightIcon className="w-5 h-5" data-id="element-274" />
              </motion.button>
            </motion.div>
            <motion.div className="mt-4 sm:mt-5" variants={itemVariants} data-id="element-275">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto" data-id="element-276">
                <div className="w-full sm:w-auto" data-id="element-277">
                  <p className="text-center text-sm font-semibold text-white mb-2" data-id="element-278">
                    Click the button below
                  </p>
                  <motion.button data-tf-popup="F3YtunzA" onClick={() => {
                  if ((window as any).tf && (window as any).tf.createPopup) {
                    ;
                    (window as any).tf.createPopup('F3YtunzA').open();
                  }
                }} className="w-full py-2.5 px-6 text-base sm:text-lg bg-[#7fe0c3] hover:bg-[#6dd4b3] text-white font-bold rounded-xl transition-all duration-300 shadow-xl" whileHover={{
                  scale: 1.05,
                  boxShadow: '0 10px 25px -5px rgba(127, 224, 195, 0.5)'
                }} whileTap={{
                  scale: 0.98
                }} data-id="element-279">
                    Fill out form
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.main>
        </div>
      </HeroHighlight>
      <ConfirmationDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} onConfirm={handleConfirm} title="Did you fully complete this step?" message="You need to complete this step before proceeding to the next one." data-id="element-280" />
    </div>;
}